import React, { useState } from 'react'
import { Box, Button, MenuItem, styled, TextField, Typography } from '@mui/material'

import { DESCRIPTION_ALIAS_OPTIONS, DescriptionAlias } from './aliasOptions'

interface BulkAliasBarProps {
  selectedCount: number
  accountCount: number
  onApply: (alias: DescriptionAlias) => void
  onClear: () => void
  isApplying?: boolean
}

// Sets one alias on every selected transaction. Shown only while something is selected.
export const BulkAliasBar: React.FC<BulkAliasBarProps> = ({
  selectedCount,
  accountCount,
  onApply,
  onClear,
  isApplying = false,
}) => {
  const [alias, setAlias] = useState<DescriptionAlias | ''>('')

  const noun = selectedCount === 1 ? 'transaction' : 'transactions'
  const accounts = accountCount === 1 ? '1 account' : `${accountCount} accounts`

  return (
    <Bar role="region" aria-label="Bulk update description alias">
      <Summary>
        {selectedCount} {noun} selected across {accounts}
      </Summary>

      <AliasField
        select
        size="small"
        label="Description alias"
        value={alias}
        onChange={event => setAlias(event.target.value as DescriptionAlias)}
      >
        {DESCRIPTION_ALIAS_OPTIONS.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </AliasField>

      {/* The bank service rejects an empty alias, so there is nothing to send until one is picked. */}
      <Button
        variant="contained"
        size="small"
        disabled={!alias || isApplying}
        onClick={() => alias && onApply(alias)}
      >
        Apply alias
      </Button>

      <Button size="small" onClick={onClear} disabled={isApplying}>
        Clear selection
      </Button>
    </Bar>
  )
}

const Bar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1, 2),
  border: '1px solid #E4E7EC',
  borderRadius: '8px',
  backgroundColor: '#F9FAFB',
}))

const Summary = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 500,
  color: theme.palette.text.primary,
  marginRight: theme.spacing(1),
}))

const AliasField = styled(TextField)({
  minWidth: 200,
  backgroundColor: '#FFFFFF',
})
